/* ==========================================================================
   AGDEL — Moteur de motion  (Itération 1/4) — Vanilla JS, zéro dépendance
   À charger dans layout/theme.liquid, avant </body> :
   <script src="{{ 'agdel-motion.js' | asset_url }}" defer></script>

   API (attributs HTML) :
     data-reveal | "mask" | "lines"   apparitions au scroll
     data-reveal-delay="200"          délai (ms)
     data-magnetic="0.35"             force de l'effet magnétique
     data-parallax="0.06"             intensité de la parallaxe
     data-cursor="Découvrir"          le curseur affiche ce mot
     data-cursor-hover                le curseur devient un cercle évidé
   Ré-initialisable après un rendu dynamique : window.AgdelMotion.init(root)
   ========================================================================== */
(() => {
  'use strict';

  const root = document.documentElement;
  root.classList.add('js');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const lerp = (a, b, t) => a + (b - a) * t;
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  /* ------------------------------------------------------------------ *
   * 1. CURSEUR PERSONNALISÉ
   * ------------------------------------------------------------------ */
  const Cursor = (() => {
    if (!finePointer) return { bind() {} };

    const el = document.createElement('div');
    el.className = 'agdel-cursor is-hidden';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<div class="agdel-cursor__dot"><span class="agdel-cursor__label"></span></div>';
    document.body.appendChild(el);
    root.classList.add('has-agdel-cursor');

    const label = el.querySelector('.agdel-cursor__label');
    const pos = { x: -100, y: -100 };    // position réelle de la souris
    const cur = { x: -100, y: -100 };    // position lissée
    let raf = null;
    const EASE = 0.16;                   // « délai physique » : plus petit = plus lourd

    const loop = () => {
      cur.x = lerp(cur.x, pos.x, EASE);
      cur.y = lerp(cur.y, pos.y, EASE);
      el.style.transform = `translate3d(${cur.x}px, ${cur.y}px, 0)`;
      raf = requestAnimationFrame(loop);
    };

    window.addEventListener('mousemove', (e) => {
      pos.x = e.clientX; pos.y = e.clientY;
      if (el.classList.contains('is-hidden')) {
        cur.x = pos.x; cur.y = pos.y;   // pas de « glissade » au premier mouvement
        el.classList.remove('is-hidden');
      }
      if (!raf) raf = requestAnimationFrame(loop);
    }, { passive: true });

    document.addEventListener('mouseleave', () => el.classList.add('is-hidden'));
    document.addEventListener('mouseenter', () => el.classList.remove('is-hidden'));
    window.addEventListener('mousedown', () => el.classList.add('is-down'));
    window.addEventListener('mouseup', () => el.classList.remove('is-down'));

    // Délégation : fonctionne aussi pour le contenu ajouté dynamiquement
    const HOVER_SEL = 'a, button, label, summary, input, select, [data-cursor-hover]';
        // Zones sombres : le curseur passe en crème pour rester visible
    const DARK_SEL = '.agdel-header, .agdel-footer, .agdel-hero, .agdel-loader, [data-cursor-dark]';
    document.addEventListener('mouseover', (e) => {
      el.classList.toggle('is-dark', !!e.target.closest(DARK_SEL));
      const withWord = e.target.closest('[data-cursor]');
      if (withWord) {
        label.textContent = withWord.dataset.cursor;
        el.classList.add('is-label'); el.classList.remove('is-hover');
        return;
      }
      el.classList.remove('is-label');
      el.classList.toggle('is-hover', !!e.target.closest(HOVER_SEL));
    }, { passive: true });

    return { bind() {} };
  })();

  /* ------------------------------------------------------------------ *
   * 2. APPARITIONS (IntersectionObserver)
   * ------------------------------------------------------------------ */
  let revealIO = null;

  // Découpe un titre en lignes masquées : chaque <br> devient une ligne
  const splitLines = (node) => {
    if (node.dataset.split) return;
    node.dataset.split = '1';
    const parts = node.innerHTML.split(/<br\s*\/?>/i);
    node.innerHTML = parts
      .map((html, i) => `<span class="agdel-line"><span style="--line-i:${i}">${html.trim()}</span></span>`)
      .join('');
  };

  const initReveal = (scope) => {
    const nodes = scope.querySelectorAll('[data-reveal]:not([data-reveal-bound])');
    if (!nodes.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      nodes.forEach((n) => n.classList.add('is-visible'));
      return;
    }

    revealIO = revealIO || new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealIO.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

    nodes.forEach((n) => {
      n.setAttribute('data-reveal-bound', '');
      const delay = parseInt(n.dataset.revealDelay || '0', 10);
      if (delay) n.style.setProperty('--reveal-delay', delay);
      if (n.dataset.reveal === 'lines') splitLines(n);
      revealIO.observe(n);
    });
  };

  /* ------------------------------------------------------------------ *
   * 3. MAGNÉTISME DES BOUTONS
   * Le bouton suit le curseur (force = data-magnetic), son texte un peu moins
   * (géré en CSS via --mx/--my). Le point d'entrée alimente aussi le
   * remplissage progressif du bouton secondaire (--fx/--fy).
   * ------------------------------------------------------------------ */
  const initMagnetic = (scope) => {
    if (!finePointer || reduceMotion) return;

    scope.querySelectorAll('[data-magnetic]:not([data-magnetic-bound])').forEach((btn) => {
      btn.setAttribute('data-magnetic-bound', '');
      const strength = parseFloat(btn.dataset.magnetic) || 0.35;
      const RANGE = 28; // zone d'attraction au-delà des bords (px)

      const setFill = (e) => {
        const r = btn.getBoundingClientRect();
        btn.style.setProperty('--fx', `${((e.clientX - r.left) / r.width) * 100}%`);
        btn.style.setProperty('--fy', `${((e.clientY - r.top) / r.height) * 100}%`);
      };

      btn.addEventListener('mouseenter', (e) => { setFill(e); btn.classList.add('is-magnetic-active'); });

      btn.addEventListener('mousemove', (e) => {
        const r = btn.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        btn.style.setProperty('--mx', `${clamp(dx * strength, -RANGE, RANGE)}px`);
        btn.style.setProperty('--my', `${clamp(dy * strength, -RANGE, RANGE)}px`);
      }, { passive: true });

      btn.addEventListener('mouseleave', (e) => {
        setFill(e); // le remplissage se retire vers la sortie
        btn.classList.remove('is-magnetic-active');
        btn.style.setProperty('--mx', '0px');
        btn.style.setProperty('--my', '0px');
      });
    });
  };

  /* ------------------------------------------------------------------ *
   * 4. PARALLAXE DOUX (un seul rAF partagé, éléments visibles uniquement)
   * ------------------------------------------------------------------ */
  const parallaxItems = new Set();
  let parallaxIO = null;
  let ticking = false;

  const updateParallax = () => {
    ticking = false;
    const vh = window.innerHeight;
    parallaxItems.forEach((item) => {
      const r = item.el.getBoundingClientRect();
      // -1 (haut de l'écran) → 1 (bas de l'écran), 0 au centre
      const progress = clamp(((r.top + r.height / 2) - vh / 2) / (vh / 2 + r.height / 2), -1, 1);
      item.el.style.setProperty('--py', `${(progress * item.strength * r.height * -1).toFixed(2)}px`);
    });
  };
  const requestParallax = () => { if (!ticking) { ticking = true; requestAnimationFrame(updateParallax); } };

  const initParallax = (scope) => {
    if (reduceMotion) return;
    const nodes = scope.querySelectorAll('[data-parallax]:not([data-parallax-bound])');
    if (!nodes.length) return;

    parallaxIO = parallaxIO || new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const item = [...parallaxItems].find((i) => i.el === entry.target);
        if (item) item.visible = entry.isIntersecting;
      });
      // On ne garde que les éléments visibles dans la boucle de calcul
      requestParallax();
    }, { rootMargin: '10% 0px' });

    nodes.forEach((el) => {
      el.setAttribute('data-parallax-bound', '');
      // Amplitude limitée à 12 % pour rester « très léger »
      const strength = clamp(parseFloat(el.dataset.parallax) || 0.06, 0, 0.12);
      parallaxItems.add({ el, strength, visible: true });
      parallaxIO.observe(el);
    });

    window.addEventListener('scroll', requestParallax, { passive: true });
    window.addEventListener('resize', requestParallax, { passive: true });
    requestParallax();
  };

  /* ------------------------------------------------------------------ *
   * 5. IMAGES BLUR-UP
   * L'image se charge en lazy, puis passe de flou à nette.
   * ------------------------------------------------------------------ */
  const initBlurUp = (scope) => {
    scope.querySelectorAll('img.agdel-blurup:not([data-blurup-bound])').forEach((img) => {
      img.setAttribute('data-blurup-bound', '');
      const done = () => requestAnimationFrame(() => img.classList.add('is-loaded'));
      if (img.complete && img.naturalWidth) done();
      else { img.addEventListener('load', done, { once: true }); img.addEventListener('error', done, { once: true }); }
    });
  };

  /* ------------------------------------------------------------------ *
   * INIT
   * ------------------------------------------------------------------ */
  const init = (scope = document) => {
    initBlurUp(scope);
    initReveal(scope);
    initMagnetic(scope);
    initParallax(scope);
  };

  window.AgdelMotion = { init };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Support de l'éditeur de thème Shopify (sections rechargées à chaud)
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
